// ─────────────────────────────────────────────────────────────────────────
// WelcomeModal — fires once for new users on first dashboard paint.
// Three slides: what CIMA is → the 5 phases → pick your path.
// Skippable everywhere; "Skip" closes the modal and marks welcome_seen
// so it doesn't reappear next session.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { useTour } from './TourProvider';
import { useTeamsStatus } from '../hooks/useTeamsStatus';

interface Props {
  role: 'ADMIN' | 'MANAGER' | 'USER' | null | undefined;
  displayName?: string | null;
  /** Callback to deep-link into Settings → Integrations. Used when an
   *  admin lands on a fresh instance and needs to configure Teams. */
  onOpenIntegrations?: () => void;
}

export function WelcomeModal({ role, displayName, onOpenIntegrations }: Props) {
  const { state, loaded, markWelcomeSeen, start } = useTour();
  const teamsStatus = useTeamsStatus();
  const [slide, setSlide] = useState(0);
  const [open,  setOpen]  = useState(false);

  const isAdmin     = role === 'ADMIN' || role === 'MANAGER';
  const teamsNeeded = isAdmin && teamsStatus.loaded && !teamsStatus.isReady;

  // Decide whether to open — runs after onboarding state has loaded.
  useEffect(() => {
    if (!loaded) return;
    if (!state) return;
    if (state.welcome_seen_at)  return;   // already seen
    if (state.completed_at)     return;   // back-fill auto-marked
    // Defer 250 ms so the dashboard paints first — modal feels like an
    // overlay on a real workspace, not a wall blocking entry.
    const t = setTimeout(() => setOpen(true), 250);
    return () => clearTimeout(t);
  }, [loaded, state]);

  // Keyboard: Esc closes, ←/→ navigate slides
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape')      { e.preventDefault(); close(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setSlide((s) => Math.min(s + 1, 2)); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); setSlide((s) => Math.max(s - 1, 0)); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function close() {
    setOpen(false);
    markWelcomeSeen().catch(() => {});
  }

  function startTour() {
    close();
    // small delay so close animation settles before the tour overlay paints
    setTimeout(() => start('dashboard'), 150);
  }

  if (!open) return null;

  const slides = [
    {
      eyebrow: 'Welcome',
      title:   `${displayName ? `Hi ${displayName.split(' ')[0]}` : 'Welcome'} — this is CIMA.`,
      body:    'CIMA (Crisis & Incident Management App) helps your team **raise, manage, and sign off** critical incidents in one place. Built around the **CIMT** (Crisis Incident Management Team) model.',
      visual:  'shield',
    },
    {
      eyebrow: 'How it works',
      title:   'Every incident runs through five phases.',
      body:    '**Report** → **SitRep** (chat with the agent) → **IAP** (Incident Action Plan) → **Execute** → **Sign-off**. The same workspace handles all five — no jumping between tools.',
      visual:  'phases',
    },
    {
      eyebrow: teamsNeeded ? 'One setup step first' : 'Get going',
      title:   teamsNeeded
        ? 'Before incidents — connect Microsoft Teams.'
        : isAdmin ? 'A 60-second walkthrough?' : 'A quick tour?',
      body:    teamsNeeded
        ? 'CIMA needs your organisation\'s Teams to schedule meetings and pull transcripts. **Until it\'s connected, no incidents can be raised.** Takes ~15 minutes — we\'ll walk you through it. You can also still take the dashboard tour first if you prefer.'
        : isAdmin
          ? 'Take the walkthrough and we\'ll show you every tile — including admin tools like **Staff & Roster** and the **Weekly Roster grid**. You can re-run it anytime from the **?** button next to *Dashboard*.'
          : 'Take the walkthrough and we\'ll show you every part of your dashboard. You can re-run it anytime from the **?** button next to *Dashboard*.',
      visual:  'pickpath',
    },
  ];

  const cur = slides[slide];
  const isLast = slide === slides.length - 1;

  return (
    <>
      <div
        aria-hidden="true"
        onClick={close}
        style={{
          position: 'fixed', inset: 0, zIndex: 1006,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(4px)',
          animation: 'cimaWelcomeBackdropIn 0.25s ease-out',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cima-welcome-title"
        style={{
          position: 'fixed', zIndex: 1007,
          top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 'min(520px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - 40px)',
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 18,
          boxShadow: '0 24px 64px rgba(15,23,42,0.32)',
          overflow: 'hidden',
          fontFamily: 'inherit', color: 'var(--fg)',
          animation: 'cimaWelcomeIn 0.3s ease-out',
        }}
      >
        <style>{`
          @keyframes cimaWelcomeBackdropIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes cimaWelcomeIn { from { opacity: 0; transform: translate(-50%, -46%) scale(0.97); } to { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
          @media (prefers-reduced-motion: reduce) {
            @keyframes cimaWelcomeBackdropIn { from, to { opacity: 1; } }
            @keyframes cimaWelcomeIn { from, to { opacity: 1; transform: translate(-50%, -50%); } }
          }
        `}</style>

        {/* Hero band */}
        <div style={{
          position: 'relative',
          background: '#0153D4',
          padding: '28px 28px 36px',
          color: 'white',
          overflow: 'hidden',
        }}>
          <div aria-hidden="true" style={{
            position: 'absolute', top: -60, right: -60, width: 220, height: 220,
            borderRadius: '50%',
            background: 'transparent',
          }} />
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '4px 10px', borderRadius: 999,
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.22)',
            fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase',
            marginBottom: 14, position: 'relative', zIndex: 1,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%', background: '#86EFAC',
              animation: 'cimaCriticalDotPulse 1.8s infinite',
            }} />
            {cur.eyebrow}
          </div>
          {slide === 1 ? <PhasesVisual /> : slide === 0 ? <ShieldVisual /> : <PickPathVisual />}
          <h2 id="cima-welcome-title" style={{
            margin: '14px 0 0', fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em',
            lineHeight: 1.25, position: 'relative', zIndex: 1,
          }}>{cur.title}</h2>
        </div>

        {/* Body */}
        <div style={{ padding: '22px 28px 8px' }}>
          <p style={{
            margin: 0, fontSize: 14.5, lineHeight: 1.65, color: 'var(--fg-soft)',
          }} dangerouslySetInnerHTML={{ __html: renderMd(cur.body) }} />
        </div>

        {/* Footer */}
        <div style={{
          padding: '18px 22px 20px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <button
            onClick={close}
            style={{
              background: 'transparent', color: 'var(--muted)',
              border: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', padding: '8px 4px',
            }}
          >Skip for now</button>

          {/* Progress dots */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: 5 }}>
            {slides.map((_, i) => (
              <span key={i} style={{
                width: i === slide ? 20 : 6,
                height: 6, borderRadius: 4,
                background: i === slide ? 'var(--primary)' : i < slide ? 'var(--primary-border)' : 'var(--border)',
                transition: 'all 0.25s',
              }} />
            ))}
          </div>

          {slide > 0 && (
            <button
              onClick={() => setSlide(slide - 1)}
              style={{
                background: 'var(--bg)', color: 'var(--fg)',
                border: '1px solid var(--border)', borderRadius: 10,
                padding: '9px 14px', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                cursor: 'pointer',
              }}
            >Back</button>
          )}

          {isLast ? (
            <>
              {teamsNeeded && onOpenIntegrations && (
                <button
                  onClick={startTour}
                  style={{
                    background: 'var(--bg)', color: 'var(--fg)',
                    border: '1px solid var(--border)', borderRadius: 10,
                    padding: '10px 14px', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >Tour first</button>
              )}
              <button
                onClick={() => {
                  if (teamsNeeded && onOpenIntegrations) {
                    close();
                    setTimeout(() => onOpenIntegrations(), 150);
                  } else {
                    startTour();
                  }
                }}
                style={{
                  background: 'var(--primary)',
                  color: 'white', border: 'none', borderRadius: 10,
                  padding: '10px 18px', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(99,55,138,0.32)',
                }}
              >
                {teamsNeeded && onOpenIntegrations ? 'Set up Teams now →' : 'Start walkthrough'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setSlide(slide + 1)}
              style={{
                background: 'var(--primary)',
                color: 'white', border: 'none', borderRadius: 10,
                padding: '10px 18px', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(99,55,138,0.32)',
              }}
            >Next →</button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Hero visuals (inline SVG, no asset deps) ──────────────────────────

function ShieldVisual() {
  return (
    <div aria-hidden="true" style={{ position: 'relative', zIndex: 1, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <div style={{
        width: 72, height: 72, borderRadius: 16,
        background: 'rgba(255,255,255,0.14)',
        border: '1px solid rgba(255,255,255,0.22)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 36,
      }}>🛡</div>
    </div>
  );
}

function PhasesVisual() {
  const phases = ['Report', 'SitRep', 'IAP', 'Execute', 'Sign-off'];
  return (
    <div aria-hidden="true" style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {phases.map((p, i) => (
        <React.Fragment key={p}>
          <div style={{
            padding: '5px 11px', borderRadius: 999,
            background: 'rgba(255,255,255,0.16)',
            border: '1px solid rgba(255,255,255,0.22)',
            fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3,
          }}>{p}</div>
          {i < phases.length - 1 && (
            <span style={{ opacity: 0.5, fontSize: 12 }}>→</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function PickPathVisual() {
  return (
    <div aria-hidden="true" style={{
      position: 'relative', zIndex: 1, height: 72,
      display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10,
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16,
        background: 'rgba(255,255,255,0.14)',
        border: '1px solid rgba(255,255,255,0.22)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 30,
      }}>🧭</div>
      <div style={{
        height: 1, flex: 1, background: 'rgba(255,255,255,0.25)',
      }} />
    </div>
  );
}

function renderMd(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>');
}
